import { useState, useEffect, useCallback } from 'react';
import { Box, Button, Input, Text, VStack, HStack, Card, Badge, Flex } from '@chakra-ui/react';
import { Plus, X, MagnifyingGlass } from '@phosphor-icons/react';

import { fetchAPI } from '@/api/fetch';
import {
  apiGetCandidateAvailableJobs,
  GetCandidateAvailableJobsResponse
} from '@/api/services/candidate.service';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { JobBasicInfo } from '@/types/Job';
import { getEmploymentTypeLabel, getWorkTypeLabel } from '@/types/enums';

interface AddCandidateToJobDialogProps {
  candidateId: string;
  candidateName: string;
  buttonText?: string;
  onAddToJob: (jobId: string) => Promise<void>;
}

export default function AddCandidateToJobDialog({
  candidateId,
  candidateName,
  onAddToJob,
  buttonText = 'Add to Job'
}: AddCandidateToJobDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [jobs, setJobs] = useState<JobBasicInfo[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [addingToJob, setAddingToJob] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalJobs, setTotalJobs] = useState(0);

  const loadAvailableJobs = useCallback(
    async (page: number, keyword: string, append = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoadingJobs(true);
      }

      try {
        const response = await fetchAPI<GetCandidateAvailableJobsResponse>({
          request: () =>
            apiGetCandidateAvailableJobs({
              candidateId,
              params: {
                page,
                limit: 20,
                keyword: keyword.trim()
              }
            })
        });

        if (response.success) {
          if (append) {
            setJobs((prevJobs) => [...prevJobs, ...response.data.jobs]);
          } else {
            setJobs(response.data.jobs);
          }
          setTotalJobs(response.data.total_count);
          setCurrentPage(response.data.current_page);
          setHasNextPage(response.data.current_page < response.data.total_pages);
        }
      } catch (error) {
        console.error('Failed to load available jobs:', error);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoadingJobs(false);
        }
      }
    },
    [candidateId]
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isDialogOpen) {
      loadAvailableJobs(1, '');
    }
  }, [isDialogOpen, loadAvailableJobs]);

  useEffect(() => {
    if (isDialogOpen) {
      loadAvailableJobs(1, debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, isDialogOpen, loadAvailableJobs]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // If we're near the bottom (within 50px) and there's a next page and not already loading
      if (scrollBottom < 50 && hasNextPage && !loadingMore && !loadingJobs) {
        loadAvailableJobs(currentPage + 1, debouncedSearchQuery, true);
      }
    },
    [hasNextPage, loadingMore, loadingJobs, currentPage, debouncedSearchQuery, loadAvailableJobs]
  );

  const handleAddToJob = async (jobId: string) => {
    setAddingToJob(jobId);
    try {
      await onAddToJob(jobId);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to add candidate to job:', error);
    } finally {
      setAddingToJob(null);
    }
  };

  const handleDialogClose = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setJobs([]);
    setCurrentPage(1);
    setHasNextPage(false);
    setLoadingMore(false);
    setIsDialogOpen(false);
  };

  const getCreatedDateFromNow = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <DialogRoot
      open={isDialogOpen}
      onOpenChange={({ open }) => setIsDialogOpen(open)}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      unmountOnExit
    >
      <DialogTrigger asChild>
        <Button
          bg="primary.900"
          color="neutrals.0"
          borderRadius="8px"
          fontSize="16px"
          fontWeight="medium"
          _hover={{ bg: 'primary.700' }}
        >
          <Plus size={16} weight="bold" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent borderRadius="12px" maxW="800px" maxH="90vh">
        <DialogHeader>
          <DialogTitle>Add {candidateName} to Job</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap="24px" align="stretch">
            <Field>
              <Box position="relative" w="100%">
                <Input
                  placeholder="Search by job title or company"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  paddingLeft="40px"
                  size="lg"
                />
                <Box position="absolute" left="12px" top="50%" transform="translateY(-50%)">
                  <MagnifyingGlass size={20} color="#6B7280" />
                </Box>
              </Box>
            </Field>

            <Box>
              <Text fontSize="16px" fontWeight="semibold" color="neutrals.800" mb="16px">
                Available Jobs ({totalJobs})
              </Text>

              {loadingJobs ? (
                <VStack gap="12px" w="100%">
                  {[1, 2].map((i) => (
                    <Card.Root key={i} p="20px" w="100%">
                      <Flex gap="16px" align="center" justify="space-between">
                        <Skeleton flex="3" h="20px" />
                        <Skeleton flex="1" h="36px" />
                      </Flex>
                      <SkeletonText noOfLines={2} mt="12px" />
                    </Card.Root>
                  ))}
                </VStack>
              ) : (
                <Box maxH="400px" overflowY="auto" onScroll={handleScroll}>
                  <VStack gap="12px" align="stretch">
                    {jobs.length === 0 ? (
                      <Text color="neutrals.500" textAlign="center" py="40px" fontSize="16px">
                        {searchQuery ? 'No jobs match your search criteria' : 'No available jobs'}
                      </Text>
                    ) : (
                      <>
                        {jobs.map((job) => (
                          <Card.Root
                            key={job.job_id}
                            bg="neutrals.50"
                            borderRadius="8px"
                            p="16px"
                            cursor="pointer"
                            transition="all 0.2s ease-in-out"
                          >
                            <Flex justify="space-between" align="center">
                              <VStack align="flex-start" gap="8px" flex="1">
                                <Flex gap="12px" align="center">
                                  <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
                                    {job.job_title}
                                  </Text>
                                  <Badge
                                    bg="primary.100"
                                    color="primary.900"
                                    px="12px"
                                    py="2px"
                                    borderRadius="12px"
                                    fontSize="12px"
                                    fontWeight="medium"
                                  >
                                    {job.unique_id}
                                  </Badge>
                                </Flex>

                                <Text fontSize="14px" color="neutrals.700">
                                  {job.client.name}
                                </Text>

                                <HStack gap="12px">
                                  <Badge
                                    bg="neutrals.200"
                                    color="neutrals.700"
                                    px="8px"
                                    py="2px"
                                    borderRadius="4px"
                                    fontSize="12px"
                                  >
                                    {getWorkTypeLabel(job.work_type)}
                                  </Badge>
                                  <Badge
                                    bg="neutrals.200"
                                    color="neutrals.700"
                                    px="8px"
                                    py="2px"
                                    borderRadius="4px"
                                    fontSize="12px"
                                  >
                                    {getEmploymentTypeLabel(job.employment_type)}
                                  </Badge>
                                  <Text fontSize="12px" color="neutrals.500">
                                    {getCreatedDateFromNow(job.created_at)} days active
                                  </Text>
                                </HStack>
                              </VStack>

                              <Button
                                p="8px"
                                bg="neutrals.0"
                                color="primary.900"
                                border="1px solid"
                                borderColor="primary.900"
                                borderRadius="full"
                                _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
                                loading={addingToJob === job.job_id}
                                onClick={() => handleAddToJob(job.job_id)}
                              >
                                <Plus size={14} weight="bold" />
                              </Button>
                            </Flex>
                          </Card.Root>
                        ))}

                        {loadingMore && (
                          <Card.Root p="20px" w="100%">
                            <Flex gap="16px" align="center" justify="center">
                              <Skeleton w="100%" h="60px" />
                            </Flex>
                          </Card.Root>
                        )}
                      </>
                    )}
                  </VStack>
                </Box>
              )}
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              w="120px"
              borderColor="primary.900"
              background="background.secondary"
              color="primary.900"
              borderRadius="8px"
              fontSize="16px"
              disabled={loadingJobs || addingToJob !== null}
            >
              Close
            </Button>
          </DialogActionTrigger>
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={loadingJobs || addingToJob !== null}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
